import { cva, type VariantProps } from 'class-variance-authority'
import type { ReactNode } from 'react'
import s from './EmptyState.module.css'

/**
 * État vide / contenu d'attente — le même motif du prototype sous ses deux formes :
 * centré dans une zone de résultats (« Aucune demande »), ou aligné à gauche dans un
 * panneau (« Onglet à construire »).
 *
 * Un écran qui n'a jamais vu son état vide a un layout faux (M21 côté UI) : ce
 * composant rend cet état aussi simple à poser que la liste pleine.
 */
const emptyState = cva(s.root, {
  variants: {
    align: {
      center: s.alignCenter,
      start: s.alignStart,
    },
  },
  defaultVariants: { align: 'center' },
})

export interface EmptyStateProps extends VariantProps<typeof emptyState> {
  /** Illustration au-dessus du titre — typiquement une icône Lucide, `aria-hidden`. */
  icon?: ReactNode
  title: ReactNode
  /** Texte d'accompagnement : quoi faire, ou ce qui viendra. */
  children?: ReactNode
  className?: string
}

export function EmptyState({ icon, title, children, align, className }: EmptyStateProps) {
  return (
    <div className={emptyState({ align, className })}>
      {icon !== undefined && (
        <span className={s.icon} aria-hidden>
          {icon}
        </span>
      )}
      <p className={s.title}>{title}</p>
      {children !== undefined && <p>{children}</p>}
    </div>
  )
}
